<script setup lang="ts">
import type { Rule } from "async-validator";
import { CUSTOM_VALUE } from "~/modules/globalDeviceConfiguration/constants";
import type { Option } from "~/modules/globalDeviceConfiguration/models/Option.interface";
import ILxCross from "~icons/lxc-business/cross";
import ILxcCheck from "~icons/lxc/check";

const props = defineProps<{
  prop: string;
  itemLabel: string;
  customValue?: number;
  selectedValue?: number;
  unitLabel: string;
  rules: Rule[];
  options?: Option[];
  min?: number;
  max?: number;
  disabled?: boolean;
}>();

const emit = defineEmits([
  "update:customValue",
  "update:selectedValue",
  "validateCustom",
  "cancelCustom",
]);

const { t } = useI18n();

const formCustomValueRef = ref();

const customValueComputed = computed({
  get() {
    return props.customValue;
  },
  set(customValue) {
    emit("update:customValue", customValue);
  },
});

const selectedValueComputed = computed({
  get() {
    return props.selectedValue;
  },
  set(selectedValue) {
    emit("update:selectedValue", selectedValue);
  },
});

const formCustomValueModel = computed(() => {
  return {
    customValue: customValueComputed.value,
  };
});

const formSelectedValueModel = computed(() => {
  return {
    selectedValue: selectedValueComputed.value,
  };
});
</script>

<template>
  <lxc-form
    v-if="selectedValue === CUSTOM_VALUE"
    ref="formCustomValueRef"
    :rules="{ customValue: rules }"
    :model="formCustomValueModel"
  >
    <!-- Custom value -->
    <lxc-form-item prop="customValue" :label="itemLabel">
      <div class="flex flex-wrap space-x-2">
        <lxc-input
          v-model="customValueComputed"
          type="number"
          :min="min"
          :max="max"
          suffix-read-only
          placeholder=" "
        >
          <template #suffix>{{ unitLabel }}</template>
          <template #after>
            <div class="flex gap-2 ml-2">
              <lxc-button
                type="primary"
                :title="t('button.validate')"
                :icon="ILxcCheck"
                icon-class="!mr-0"
                :disabled="!formCustomValueRef?.isValid"
                @click.prevent="emit('validateCustom')"
              />

              <lxc-button
                type="secondary"
                :title="t('button.cancel')"
                :icon="ILxCross"
                icon-class="!mr-0"
                @click.prevent="emit('cancelCustom')"
              />
            </div>
          </template>
        </lxc-input>
      </div>
    </lxc-form-item>
  </lxc-form>

  <!-- Select values -->
  <lxc-form
    v-else
    :rules="{ selectedValue: rules }"
    :model="formSelectedValueModel"
  >
    <lxc-form-item prop="selectedValue" :label="itemLabel" :disabled="disabled">
      <lxc-select v-model="selectedValueComputed">
        <lxc-option
          v-for="option of options || []"
          :key="option.value"
          :value="option.value"
        >
          {{ option.text }}</lxc-option
        >
      </lxc-select>
    </lxc-form-item>
  </lxc-form>
</template>
